import { useCallback, useContext, useMemo } from 'react';
import FlyersContext from '@/context/FlyersContext';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BottomViewFlyers,
  HeaderFixed,
  FlyerboardPagination,
  PageTitleHelmet,
} from '@/components';
import { sortBy } from 'lodash';
import { FlyerType, SubscriptionTypeEnum } from '@/config/constants';
import scrollToTop from '@/utils/scroolToTop';
import { useAnalytics } from '@/hooks/useAnalytics';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import {
  FlyerboardHeader,
  NavBarBackButton,
  NavbarSwitcher,
} from '@/design-system';
import { useReactiveVar } from '@apollo/client';
import { openLoginDrawerVar, subscriptionsVar } from '@/graphql/state';
import { AllFlyerFilters, OwningTypeEnum } from '@/graphql/types';
import AuthenticationContext from '@/context/AuthenticationContext';
import { useEffectOnMount, useURLSearchParams } from '@/hooks';
import styles from './ExploreAllFlyerboard.styles';

const communityCategoriesDisabled = ['school_events', 'student_organization'];
const schoolCategoriesDisabled = ['family_activities_and_centers', 'service_learning'];

const categorySportsMappings = {
  'sports_and_recreation': {
    [FlyerType.school]: 'athletics',
  },
  'athletics': {
    [FlyerType.community]: 'sports_and_recreation',
  },
};

export const ExploreAllFlyerboard: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthenticationContext);

  const { districtId, audienceId, audienceType, categoryId, categoryName } =
    useURLSearchParams();

  const pageTitle = categoryName || 'All Flyers';
  const { track } = useAnalytics();
  const { activeTab, onChangeFlyerType: _onChangeFlyerType } =
    useContext(FlyersContext);
  const subscriptions = useReactiveVar(subscriptionsVar);
  const trackId = 'all_flyers_page';

  const getSubscriptionsIds = useCallback(
    () => subscriptions.map(s => Number(s.id)),
    [subscriptions],
  );

  useEffectOnMount(() => {
    scrollToTop();
  });

  const shouldDisableSwitcherTab = useMemo(() => {
    if (categoryId) {
      if (activeTab === FlyerType.school) {
        if(communityCategoriesDisabled.includes(categoryId)) {
          return FlyerType.community;
        }
      } else {
         if(schoolCategoriesDisabled.includes(categoryId)) {
          return FlyerType.school;
        }
      }
    }
    return undefined;
  }, [categoryId, activeTab]);

  const filters = useMemo((): AllFlyerFilters | null => {
    const subscriptionsIds = getSubscriptionsIds();
    if (isAuthenticated && subscriptionsIds.length === 0) return null;
    const owningType =
      activeTab === FlyerType.school
        ? OwningTypeEnum.district
        : OwningTypeEnum.organization;
    let schoolIds: number[] = [];

    const isDistrictFlyerboard = audienceType === SubscriptionTypeEnum.district;
    const isCommunityTab = activeTab === FlyerType.community;
    const isMySchools = audienceId === 'all';
    const audienceIdList = isMySchools ? [] : [Number(audienceId)];
    if (isAuthenticated) {
      if (isMySchools || (isDistrictFlyerboard && isCommunityTab)) {
        schoolIds = subscriptionsIds;
      } else {
        if(!isDistrictFlyerboard) {
          schoolIds = audienceIdList;
        }
      }
    }
   else {
    if (!isDistrictFlyerboard) {
      schoolIds = audienceIdList;
    }
   }
   const allDistrictSchools = (isDistrictFlyerboard && !isCommunityTab) || (isCommunityTab && !isAuthenticated && schoolIds.length === 0);

    let baseFilters: AllFlyerFilters = {
      owningType,
      allDistrictSchools,
      schoolIds,
    };

    if (categoryId) {
      const categoryIdFilter = categorySportsMappings[categoryId]?.[activeTab] || categoryId;

      baseFilters = {
        ...baseFilters,
        categories: [categoryIdFilter],
      };
    }

    return baseFilters;
  }, [
    audienceId,
    audienceType,
    categoryId,
    activeTab,
    getSubscriptionsIds,
    isAuthenticated,
  ]);

  const selectedSubscriptions = useMemo(
    () => sortBy(subscriptions, subscription => subscription.name),
    [subscriptions],
  );

  const onBackButtonClick = () => {
    if (isAuthenticated) {
      navigate(`/explore?tab=${activeTab}`);
    } else {
      openLoginDrawerVar(true);
    }
  };

  const onChangeFlyerType = () => {
    _onChangeFlyerType();
    scrollToTop();
  };

  const handleClickFlyerType = () => {
    track({
      category: `${trackId}:view_all_flyers:bottom_button`,
      action: GenericTrackTypes.BUTTON_PRESSED,
      label: FlyerType.school === activeTab ? 'Community' : 'School',
    });
    onChangeFlyerType();
  };

  const invertedFlyerType =
    activeTab === FlyerType.school ? 'community' : 'school';

  // const bottomLabel = categoryId ?
  // `View ${invertedFlyerType} ${categoryName?.toLocaleLowerCase()}`:
  // `View all ${invertedFlyerType} flyers`;

  const bottomLabel = `View all ${invertedFlyerType} flyers`;
  if (!filters) return null;
  return (
    <>
    <PageTitleHelmet>Explore All Flyers</PageTitleHelmet>
    <div className={styles.container}>
      <HeaderFixed>
        <NavbarSwitcher
          trackId={trackId}
          navBarTitle={pageTitle}
          onBackButtonClick={onBackButtonClick}
          disabledTab={shouldDisableSwitcherTab}
        />
        <FlyerboardHeader
          subscriptions={selectedSubscriptions}
          type={activeTab}
          selectedAudienceId={audienceId}
        />
      </HeaderFixed>
      <FlyerboardPagination
        trackId={trackId}
        flyerType={activeTab}
        flyerboardFilters={filters}
        audienceId={audienceId}
        districtId={districtId}
      />
      {categoryId ? (
        <BottomViewFlyers variant={FlyerType[activeTab]} />
      ) : (
        <BottomViewFlyers
          label={bottomLabel}
          variant={FlyerType[activeTab]}
          onClick={handleClickFlyerType}
        />
      )}
    </div>
    </>
  );
};
